import React from 'react';
import { Box } from '@material-ui/core';
import { AttachmentViewerModal, Theme } from '@orijinworks/frontend-commons';
import { ADMIN_ATTACHMENT_RENDER_CONTENT_BY_FILE_TYPE } from '../../../../../Feeds/mappers/adminAttachmentChipDisplay';
import DeleteModal from '../../../../../../shared/components/DeleteModal/DeleteModal';
import AddWidgetModal from '../../../../components/add-widget-modal';
import EditorsPlanModal from '../../../../components/editors-plan-modal';
import ExportPlanModal from '../../../../components/export-plan-modal';
import AddFormsModal from '../../../../widgets/forms/add-forms-modal';
import { CasePlanDetailHeader } from '../CasePlanDetailHeader';
import { CasePlanDetailsSections } from '../CasePlanDetailsSections';
import { MoveWidgetToSectionModal } from '../MoveWidgetToSectionModal';

const resolveOpenConfirmation = (sectionConfirmation, widgetConfirmation) =>
  [sectionConfirmation, widgetConfirmation].find((confirmation) => confirmation?.isOpen);

/**
 * View-only renders keep the Archived tab's standalone widgets — those are read-only content,
 * not a write affordance — while every mutation handler stays withheld.
 */
const resolveSectionsWidgetApi = (isViewOnly, widgetApi) =>
  isViewOnly
    ? { orphanedArchivedWidgets: widgetApi.orphanedArchivedWidgets }
    : widgetApi;

export function CasePlanDetailsContent({
  classes,
  cardProps,
  isViewOnly = false,
  isSectionsLoading,
  collapsedSections,
  onBack,
  onToggleCollapse,
  onAddWidget,
  isAddWidgetOpen = false,
  widgetTypeCategories = [],
  onAddWidgetClose,
  onAddWidgetSelect,
  onWidgetDescriptionBlur,
  onEditorsClick,
  canManageEditors = false,
  isEditorsOpen,
  onEditorsClose,
  onEditorsSaved,
  planId,
  onExportClick,
  isExportOpen,
  onExportClose,
  onExportSubmit,
  status,
  isEditor,
  onStatusSelect,
  pendingStatus,
  onStatusConfirm,
  onStatusCancel,
  sectionApi = {},
  widgetApi = {},
  attachmentViewer = null,
  onCloseAttachmentViewer,
  addFormsModal = {},
}) {
  const { activeSections = [], sectionConfirmation } = sectionApi;
  const {
    moveToSectionModal = {},
    onConfirmMoveToSection,
    onCloseMoveToSectionModal,
    widgetConfirmation,
  } = widgetApi;
  const openConfirmation = resolveOpenConfirmation(
    sectionConfirmation,
    widgetConfirmation,
  );

  return (
    <Theme>
      <Box className={classes.page} role="main" aria-label="Case Plan Details">
        <CasePlanDetailHeader
          cardProps={cardProps}
          isSectionsLoading={isSectionsLoading}
          onBack={onBack}
          onEditorsClick={canManageEditors ? onEditorsClick : undefined}
          onExportClick={onExportClick}
          status={status}
          isEditor={isEditor}
          isViewOnly={isViewOnly}
          onStatusSelect={onStatusSelect}
          pendingStatus={pendingStatus}
          onStatusConfirm={onStatusConfirm}
          onStatusCancel={onStatusCancel}
        />
        <CasePlanDetailsSections
          isSectionsLoading={isSectionsLoading}
          collapsedSections={collapsedSections}
          onToggleCollapse={onToggleCollapse}
          onAddWidget={onAddWidget}
          onWidgetDescriptionBlur={isViewOnly ? undefined : onWidgetDescriptionBlur}
          sectionApi={sectionApi}
          widgetApi={resolveSectionsWidgetApi(isViewOnly, widgetApi)}
        />
        {/* Archived sections are excluded: an export must only carry the live plan. */}
        <EditorsPlanModal
          open={isEditorsOpen}
          planId={planId}
          onClose={onEditorsClose}
          onSaved={onEditorsSaved}
        />
        <ExportPlanModal
          open={isExportOpen}
          sections={activeSections}
          onClose={onExportClose}
          onExport={onExportSubmit}
        />
        <AddWidgetModal
          open={isAddWidgetOpen}
          categories={widgetTypeCategories}
          onClose={onAddWidgetClose}
          onSelect={onAddWidgetSelect}
        />
        <AddFormsModal
          open={!isViewOnly && addFormsModal.open === true}
          forms={addFormsModal.forms}
          loading={addFormsModal.loading}
          error={addFormsModal.error}
          onClose={addFormsModal.onClose}
          onConfirm={addFormsModal.onConfirm}
        />
        {openConfirmation && (
          <DeleteModal
            open
            status={openConfirmation.modalStatus}
            title={openConfirmation.title}
            description={openConfirmation.description}
            onClose={openConfirmation.onCancel}
            onContinue={openConfirmation.onConfirm}
          />
        )}
        <MoveWidgetToSectionModal
          open={moveToSectionModal.isOpen}
          destinations={moveToSectionModal.destinations}
          onSelect={onConfirmMoveToSection}
          onClose={onCloseMoveToSectionModal}
        />
        {attachmentViewer ? (
          <AttachmentViewerModal
            fileUrl={attachmentViewer.fileUrl}
            fileName={attachmentViewer.fileName}
            fileType={attachmentViewer.fileType}
            open
            onClose={onCloseAttachmentViewer}
            renderContentByFileType={ADMIN_ATTACHMENT_RENDER_CONTENT_BY_FILE_TYPE}
          />
        ) : null}
      </Box>
    </Theme>
  );
}
